// src/components/FeedbackModal.jsx
import { useState } from "react";
import { auth, db } from "../firebase";
import {
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

// === Google Sheet webhook (Apps Script) ===
const SHEET_WEBHOOK_URL = "https://script.google.com/macros/s/AKfycbzYpLHFnwBEOjWmo0_B4VHs01gsWr5gjBRPURF_YTgpkjvs1OJvdazVoeXOAzeNreRzcw/exec";
const SHEET_TOKEN = "3671a113a0";

const TOPICS = [
  "Broken page / failed load",
  "Noticed an error",
  "Formatting issue",
  "Suggested improvement",
  "Something I like",
  "Other",
];

const SEVERITIES = [
  { label: "Does not impact use of page", priority: 1 },
  { label: "Impacts use of page", priority: 2 },
  { label: "Critical error", priority: 3 },
];

export default function FeedbackModal({ onClose }) {
  const [topic, setTopic] = useState(TOPICS[0]);
  const [severity, setSeverity] = useState(SEVERITIES[0].label);
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const sevPriority =
    SEVERITIES.find((s) => s.label === severity)?.priority ?? 1;

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
        const user = auth.currentUser;
        const storage = getStorage();

        // 1) Upload screenshot (optional)
        let imageUrl = null;
        if (file) {
        const path = `feedback/${user?.uid || "anon"}/${Date.now()}-${file.name}`;
        const ref = storageRef(storage, path);
        await uploadBytes(ref, file);
        imageUrl = await getDownloadURL(ref);
        }

        // 2) Create the feedback doc (still powers your /feedback page)
        const feedbackRef = await addDoc(collection(db, "feedback"), {
        topic,
        severity,
        priority: (SEVERITIES.find((s) => s.label === severity)?.priority ?? 1), // numeric for sorting
        description: (description || "").trim(),
        imageUrl: imageUrl || null,
        status: "open", // open | done
        createdBy: {
            uid: user?.uid || null,
            email: user?.email || null,
        },
        createdAt: serverTimestamp(),
        });

        // 3) Immediately enqueue an email by writing to /mail (Trigger Email extension)
        const feedbackId = feedbackRef.id;
        const subject = `[Trelli Feedback] ${severity} · ${topic}`;
        const appUrl = `${window.location.origin}/feedback`;

        const text = [
        `New feedback received`,
        ``,
        `Topic: ${topic}`,
        `Severity: ${severity}`,
        `Priority: ${(SEVERITIES.find((s) => s.label === severity)?.priority ?? 1)}`,
        `Submitted by: ${user?.email || "unknown@noemail"} (uid: ${user?.uid || "unknown"})`,
        ``,
        `Description:`,
        (description || "").trim() || "(no description)",
        ``,
        `Screenshot: ${imageUrl ? imageUrl : "(none)"}`,
        ``,
        `Open in Trelli: ${appUrl}`,
        `Doc ID: ${feedbackId}`,
        ].join("\n");

        const html = `
        <div style="font-family:system-ui,Segoe UI,Arial,sans-serif">
            <h2 style="margin:0 0 8px">New Trelli Feedback</h2>
            <table cellpadding="6" style="border-collapse:collapse;border:1px solid #ddd">
            <tr><td style="border:1px solid #eee"><b>Topic</b></td><td style="border:1px solid #eee">${topic}</td></tr>
            <tr><td style="border:1px solid #eee"><b>Severity</b></td><td style="border:1px solid #eee">${severity}</td></tr>
            <tr><td style="border:1px solid #eee"><b>Priority</b></td><td style="border:1px solid #eee">${(SEVERITIES.find((s) => s.label === severity)?.priority ?? 1)}</td></tr>
            <tr><td style="border:1px solid #eee"><b>Submitted by</b></td><td style="border:1px solid #eee">${user?.email || "unknown@noemail"} (uid: ${user?.uid || "unknown"})</td></tr>
            <tr><td style="border:1px solid #eee"><b>Description</b></td><td style="border:1px solid #eee;white-space:pre-wrap">${(description || "").trim() || "(no description)"}</td></tr>
            <tr><td style="border:1px solid #eee"><b>Screenshot</b></td><td style="border:1px solid #eee">${imageUrl ? `<a href="${imageUrl}">Open</a>` : "(none)"}</td></tr>
            </table>
            <p><a href="${appUrl}">Open Feedback Inbox</a></p>
            <p style="color:#888">Doc ID: ${feedbackId}</p>
        </div>
        `;

        // IMPORTANT: your working budget-alert example used an ARRAY for "to".
        await addDoc(collection(db, "mail"), {
        to: ["hello@usetrelli.com"],       // array matches your successful pattern
        replyTo: user?.email || undefined, // optional
        message: { subject, text, html },
        });

        // 4) Send to Google Sheet webhook
        try {
        const payload = {
            token: SHEET_TOKEN,
            timestamp: new Date().toISOString(),
            topic,
            severity,
            priority: (SEVERITIES.find((s) => s.label === severity)?.priority ?? 1),
            description: (description || "").trim(),
            submitterEmail: auth.currentUser?.email || "unknown@noemail",
            submitterUid: auth.currentUser?.uid || "unknown",
            imageUrl: imageUrl || "",
            feedbackId,
        };

        await fetch(SHEET_WEBHOOK_URL, {
            method: "POST",
            mode: "no-cors",                // <= important
            headers: { "Content-Type": "text/plain" },
            body: JSON.stringify(payload),
        });
        } catch (e) {
        console.warn("Sheet webhook failed:", e);
        }

        onClose?.();
    } catch (err) {
        console.error("Failed to save feedback:", err);
        alert("Sorry, we couldn’t save that. Check console for details.");
    } finally {
        setSaving(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 12000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 640,
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
          padding: 20,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>Submit MVP Feedback</h3>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: 22,
              lineHeight: 1,
            }}
            aria-label="Close"
            title="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
          <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
            Topic
          </label>
          <select
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #ccc" }}
          >
            {TOPICS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>

          <label style={{ display: "block", margin: "16px 0 8px", fontWeight: 600 }}>
            Severity of issue
          </label>
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #ccc" }}
          >
            {SEVERITIES.map((s) => (
              <option key={s.label} value={s.label}>
                {s.label}
              </option>
            ))}
          </select>

          <label style={{ display: "block", margin: "16px 0 8px", fontWeight: 600 }}>
            Describe the issue
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            placeholder="What happened? Steps to reproduce? Which page?"
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 8,
              border: "1px solid #ccc",
              resize: "vertical",
            }}
          />

          <label style={{ display: "block", margin: "16px 0 8px", fontWeight: 600 }}>
            Upload a screenshot (optional)
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />

          <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px solid #ccc",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                border: "none",
                background: saving ? "#7ac9ae" : "#00B074",
                color: "#fff",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {saving ? "Saving…" : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
